import { gql } from 'apollo-angular';

/**
 * Mutation for login.
 */
export const LOGIN_MUTATION = gql`
  mutation authenticate($username: String!, $password: String!) {
    response: authenticate(
      credentials: { username: $username, password: $password }
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for recovery link.
 */
export const RECOVERY_LINK_MUTATION = gql`
  mutation requestRecoveryLink($email: String!) {
    response: requestRecoveryLink(email: $email) {
      isSuccess
      message
    }
  }
`;

/**
 * Mutation for password recovery.
 */
export const PASSWORD_RECOVERY_MUTATION = gql`
  mutation recoverPassword($password: String!, $signature: String!) {
    response: recoverPassword(password: $password, signature: $signature) {
      isSuccess
      message
    }
  }
`;

/**
 * Mutation for updating color of the widget's header.
 */
export const HEADER_COLOR_MUTATION = gql`
  mutation updateWidgetHeaderColor(
    $boardId: String!
    $widgetId: String!
    $color: HeaderColor!
  ) {
    response: updateWidgetHeaderColor(
      boardId: $boardId
      widgetId: $widgetId
      color: $color
    ) {
      isSuccess
    }
  }
`;

/**
 * Mutation for updating title of the widget.
 */
export const WIDGET_TITLE_MUTATION = gql`
  mutation updateWidgetTitle(
    $boardId: String!
    $widgetId: String!
    $title: String!
  ) {
    response: updateWidgetTitle(
      boardId: $boardId
      widgetId: $widgetId
      title: $title
    ) {
      isSuccess
    }
  }
`;

/**
 * Mutation for deleting the widget from the board.
 */
export const WIDGET_DELETE_MUTATION = gql`
  mutation deleteWidget($boardId: String!, $widgetId: String!) {
    response: deleteWidget(boardId: $boardId, widgetId: $widgetId) {
      isSuccess
    }
  }
`;

/**
 * Mutation for widget positioning.
 */
export const WIDGET_POSITIONING_MUTATION = gql`
  mutation updateWidgetPosition(
    $widgetId: String!
    $boardId: String!
    $x: Int!
    $y: Int!
    $rows: Int!
    $cols: Int!
  ) {
    response: updateWidgetPosition(
      widgetId: $widgetId
      boardId: $boardId
      x: $x
      y: $y
      rows: $rows
      cols: $cols
    ) {
      isSuccess
    }
  }
`;

/**
 * Mutation for user invite.
 */
export const USER_INVITE_MUTATION = gql`
  mutation inviteUser(
    $firstName: String!
    $lastName: String!
    $email: String!
    $targetGroupId: String!
  ) {
    response: inviteUser(
      firstName: $firstName
      lastName: $lastName
      email: $email
      targetGroupId: $targetGroupId
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for new role creation.
 */
export const NEW_ROLE = gql`
  mutation createRole($roleName: String!, $targetGroupId: String!) {
    response: createRole(roleName: $roleName, targetGroupId: $targetGroupId) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for new group creation.
 */
export const NEW_GROUP = gql`
  mutation createGroup($groupName: String!) {
    response: createGroup(groupName: $groupName) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for new group deletion.
 */
export const DELETE_GROUP = gql`
  mutation deleteGroup($groupName: String!) {
    response: deleteGroup(groupName: $groupName) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for adding new member to the group.
 */
export const ADD_GROUP_MEMBER = gql`
  mutation addGroupMember($targetUserId: String!, $groupName: String!) {
    response: addGroupMember(
      targetUserId: $targetUserId
      groupName: $groupName
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for removing member from the group.
 */
export const REMOVE_GROUP_MEMBER = gql`
  mutation removeGroupMember($targetUserId: String!, $groupName: String!) {
    response: removeGroupMember(
      targetUserId: $targetUserId
      groupName: $groupName
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for invite acceptance.
 */
export const ACCEPT_INVITE = gql`
  mutation acceptInvite(
    $inviteId: String!
    $password: String!
    $username: String!
  ) {
    response: acceptInvite(
      inviteId: $inviteId
      password: $password
      username: $username
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for password recovery request.
 */
export const REQUEST_RECOVERY = gql`
  mutation requestRecovery($targetUserId: String!, $targetGroupId: String!) {
    response: requestRecovery(
      targetUserId: $targetUserId
      targetGroupId: $targetGroupId
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for toggling user's access.
 */
export const TOGGLE_ACCESS = gql`
  mutation toggleAccess(
    $block: Boolean!
    $targetUserId: String!
    $targetGroupId: String!
  ) {
    response: toggleAccess(
      block: $block
      targetUserId: $targetUserId
      targetGroupId: $targetGroupId
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for adding role to user.
 */
export const ADD_ROLE = gql`
  mutation addRole(
    $role: String!
    $targetUserId: String!
    $targetGroupId: String!
  ) {
    response: addRole(
      role: $role
      targetUserId: $targetUserId
      targetGroupId: $targetGroupId
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for removing role from user.
 */
export const REMOVE_ROLE = gql`
  mutation removeRole(
    $role: String!
    $targetUserId: String!
    $targetGroupId: String!
  ) {
    response: removeRole(
      role: $role
      targetUserId: $targetUserId
      targetGroupId: $targetGroupId
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for deleting role.
 */
export const DELETE_ROLE = gql`
  mutation deleteRole($role: String!, $targetGroupId: String!) {
    response: deleteRole(role: $role, targetGroupId: $targetGroupId) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for setting default role of group.
 */
export const SET_DEFAULT_ROLE = gql`
  mutation setDefaultRole($role: String!, $targetGroupId: String!) {
    response: setDefaultRole(role: $role, targetGroupId: $targetGroupId) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for adding the widget.
 */
export const ADD_WIDGET_MUTATION = gql`
  mutation addWidget($boardId: String!, $widget: WidgetCreationOptionsInput!) {
    response: addWidget(boardId: $boardId, widget: $widget) {
      isSuccess
      message
      data {
        id
        title
        boardId
        ownerId
        headerColor
        x
        y
        cols
        rows
        maxRows
        maxCols
      }
    }
  }
`;

/**
 * Mutation for adding the board.
 */
export const CREATE_BOARD_MUTATION = gql`
  mutation createBoard($board: BoardCreationOptionsInput!) {
    response: createBoard(board: $board) {
      isSuccess
      message
      data {
        id
        name
        description
        ownerId
        public
        shared
        createdAt
        groupId
      }
    }
  }
`;

/**
 * Mutation for deleting the board.
 */
export const DELETE_BOARD_MUTATION = gql`
  mutation deleteBoard($boardId: String!) {
    response: deleteBoard(boardId: $boardId) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for editing the board.
 */
export const EDIT_BOARD_MUTATION = gql`
  mutation editBoard($boardId: String!, $board: BoardEditOptionsInput!) {
    response: editBoard(boardId: $boardId, board: $board) {
      isSuccess
      message
      data {
        id
        name
        description
        ownerId
        public
        shared
        createdAt
        groupId
      }
    }
  }
`;

/**
 * Mutation for copying the board.
 */
export const COPY_BOARD_MUTATION = gql`
  mutation copyBoard(
    $templateBoardId: String!
    $board: BoardCreationOptionsInput!
  ) {
    response: copyBoard(templateBoardId: $templateBoardId, board: $board) {
      isSuccess
      message
      data {
        id
        name
        description
        ownerId
        public
        shared
        createdAt
        groupId
      }
    }
  }
`;

/**
 * Mutation for editing the board.
 */
export const CREATE_NETTO_REPORT_MUTATION = gql`
  mutation createNettoReport($report: CartItemBaseInput!) {
    response: createNettoReport(report: $report) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for editing the board.
 */
export const SAVE_NETTO_REPORT_MUTATION = gql`
  mutation saveNettoReport(
    $name: String!
    $folder: String!
    $draft: Boolean!
    $options: CartReportOptionsInput
    $reportId: String
    $description: String
  ) {
    response: saveNettoReport(
      name: $name
      folder: $folder
      draft: $draft
      options: $options
      reportId: $reportId
      description: $description
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for editing the board.
 */
export const DELETE_NETTO_REPORT_MUTATION = gql`
  mutation deleteNettoReport($reportId: String!) {
    response: deleteNettoReport(reportId: $reportId) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for creating new netto report.
 */
export const CENSUS_NG_NEW_REPORT = gql`
  mutation censusNgNewReport(
    $from: LocalDate!
    $to: LocalDate!
    $searchKeys: [Long!]
    $key: KeyType!
    $draft: Boolean!
    $id: String
  ) {
    response: censusNgNewReport(
      from: $from
      to: $to
      searchKeys: $searchKeys
      key: $key
      draft: $draft
      id: $id
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for creating new netto report folder.
 */
export const CENSUS_NG_NEW_FOLDER = gql`
  mutation censusNgNewFolder($folderName: String!) {
    response: censusNgNewFolder(folderName: $folderName) {
      isSuccess
      message
      data {
        folderName
        id
        template
        owner
        groupId
      }
    }
  }
`;

/**
 * Mutation for saving new netto report in folder.
 */
export const CENSUS_NG_SAVE_IN_FOLDER = gql`
  mutation censusNgSaveReportInFolder(
    $title: String!
    $description: String!
    $folderId: String!
    $reportId: String!
  ) {
    response: censusNgSaveReportInFolder(
      title: $title
      description: $description
      folderId: $folderId
      reportId: $reportId
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for deleting netto report in folder.
 */
export const CENSUS_NG_DELETE_REPORT_IN_FOLDER = gql`
  mutation censusNgDeleteReport($reportId: String!) {
    response: censusNgDeleteReport(reportId: $reportId) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for deleting netto folder.
 */
export const CENSUS_NG_DELETE_FOLDER = gql`
  mutation censusNgDeleteFolder($folderId: String!) {
    response: censusNgDeleteFolder(folderId: $folderId) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for deleting netto folder.
 */
export const CENSUS_NG_RENAME_FOLDER = gql`
  mutation censusNgRenameFolder($folderId: String!, $newName: String!) {
    response: censusNgRenameFolder(folderId: $folderId, newName: $newName) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for getting roles with component.
 */
export const ROLES_WITH_COMPONENT = gql`
  mutation roleComponents($targetGroupId: String!, $component: String!) {
    response: roleComponents(
      targetGroupId: $targetGroupId
      component: $component
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for granting component access to role.
 */
export const GRANT_COMPONENT = gql`
  mutation grantComponent(
    $targetGroupId: String!
    $role: String!
    $component: String!
  ) {
    response: grantComponent(
      targetGroupId: $targetGroupId
      role: $role
      component: $component
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for denying component access to role.
 */
export const DENY_COMPONENT = gql`
  mutation denyComponent(
    $targetGroupId: String!
    $role: String!
    $component: String!
  ) {
    response: denyComponent(
      targetGroupId: $targetGroupId
      role: $role
      component: $component
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for user information update.
 */
export const UPDATE_USER_INFO_MUTATION = gql`
  mutation updateUserInfo($info: AccountPrefInput!) {
    response: updateUserInfo(info: $info) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Mutation for user password update.
 */
export const UPDATE_USER_PW_MUTATION = gql`
  mutation updatePassword($oldPassword: String!, $newPassword: String!) {
    response: updatePassword(
      oldPassword: $oldPassword
      newPassword: $newPassword
    ) {
      isSuccess
      message
      data
    }
  }
`;
